import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../constants/api";
import { showError, showForbidden, promise } from "../../utils/toast.js";
import KundenFormular from "./KundenBlockFormular";
import { normalizePhone } from "../../utils/phone.js";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import AppButton from "../ui/AppButton.jsx";
import { invalidateListDomain } from "../../src/hooks/listQueryCache.js";
import { navigateToList } from "../../constants/nav.js";

/* ───────── Helpers ───────── */
const isObj = v => v && typeof v === "object" && !Array.isArray(v);
const trimStr = v => typeof v === "string" ? v.trim() : v ?? "";
const onlyDigits = v => String(v || "").replace(/\D+/g, "");
const normalizePLZ = v => onlyDigits(v).slice(0, 10);
const normalizeEmail = v => String(v || "").trim().toLowerCase();
function sanitize(form) {
  const f = {
    ...form
  };
  f.firma = trimStr(f.firma);
  f.vorname = trimStr(f.vorname);
  f.nachname = trimStr(f.nachname);
  f.strasse = trimStr(f.strasse);
  f.plz = normalizePLZ(f.plz);
  f.ort = trimStr(f.ort);
  f.telefon = normalizePhone(f.telefon);
  f.telefon2 = normalizePhone(f.telefon2);
  f.email = normalizeEmail(f.email);
  return f;
}

// Minimal-Klientvalidierung: Firma ODER (Vorname & Nachname) muss vorhanden sein
function validateClient(f) {
  const errs = [];
  if (!f.firma && !(f.vorname && f.nachname)) {
    errs.push("Bitte entweder Firmenname oder Vor- und Nachname angeben.");
  }
  if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) {
    errs.push("E-Mail ist ungültig.");
  }
  if (f.plz && f.plz.length < 4) {
    errs.push("PLZ wirkt zu kurz.");
  }
  return errs;
}
export default function KundenNeu() {
  const navigate = useNavigate();
    const confirm = useConfirm();
  const [form, setForm] = useState({
    firma: "",
    vorname: "",
    nachname: "",
    strasse: "",
    plz: "",
    ort: "",
    telefon: "",
    telefon2: "",
    email: ""
  });
  const [saving, setSaving] = useState(false);
  const [dupe, setDupe] = useState(null); // 🔍 Duplikat-Hinweis

  const initialRef = useRef(form);
  const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initialRef.current), [form]);

  // Warnung, wenn man ungespeicherte Eingaben verlässt
  useEffect(() => {
    const onBeforeUnload = e => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  // Ctrl/Cmd+S => Submit
  useEffect(() => {
    const onKey = e => {
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!saving) handleSubmit(new Event("submit"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, form]);

  // onChange: Event oder Patch-Objekt
  const handleChange = useCallback(eOrPatch => {
    setForm(prev => {
      const base = prev || {};
      if (eOrPatch && eOrPatch.target) {
        const {
          name,
          value
        } = eOrPatch.target;
        return {
          ...base,
          [name]: value
        };
      }
      if (isObj(eOrPatch)) return {
        ...base,
        ...eOrPatch
      };
      return base;
    });
  }, []);
  const handleSubmit = useCallback(async e => {
    if (e?.preventDefault) e.preventDefault();
    if (saving) return;
    const cleaned = sanitize(form);
    const errs = validateClient(cleaned);
    if (errs.length) {
      showError(errs[0]);
      return;
    }
    try {
      setSaving(true);
      setDupe(null); // alten Hinweis zurücksetzen

      const {
        data
      } = await promise(api.post("/kunden", cleaned), {
        loading: "Erstelle…",
        success: "ok.created",
        error: "srv.error"
      });
      initialRef.current = cleaned;
      invalidateListDomain("kunden");
      navigate(data?.id ? `/kunden/${data.id}` : "/kunden");
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Fehler beim Erstellen des Kunden.";
      if (status === 409) {
        const existingId = err?.response?.data?.existingId;
        const existing = err?.response?.data?.existing;
        setDupe(existingId ? {
          id: existingId,
          ...existing
        } : null);
        // Kein Toast hier – Banner übernimmt die Info
      } else if (status === 403) {
        showForbidden("default", {
          level: "error"
        });
      } else if (status === 401) {
        showError("🔒 Session abgelaufen. Bitte neu einloggen.");
      } else {
        showError(`❌ ${msg}`);
      }
      console.error("Fehler beim Erstellen:", err);
    } finally {
      setSaving(false);
    }
  }, [form, navigate, saving]);
  const handleCancel = useCallback(async () => {
    if (isDirty) {
      const {
        ok
      } = await confirm({
        title: "Eingaben verwerfen?",
        message: "Ungespeicherte Eingaben verwerfen?",
        variant: "warning",
        confirmLabel: "Verwerfen"
      });
      if (!ok) return;
    }
    navigateToList(navigate, "/kunden");
  }, [isDirty, navigate, confirm]);
  const pageCls = "min-h-dvh w-full overscroll-contain bg-gradient-to-br from-[#05070c] via-[#0a1020] to-[#0a1a32] text-white";
  const cardCls = "glass rounded-2xl border border-white/10 shadow-xl";
  return <div className={pageCls}>
      <div className="mx-auto w-full px-3 sm:px-4 py-4 sm:py-6" style={{
      paddingTop: "max(1rem, env(safe-area-inset-top))",
      paddingBottom: "max(1rem, env(safe-area-inset-bottom))"
    }}>
        <div className="mx-auto max-w-screen-sm md:max-w-3xl">
          <div className={cardCls}>
            <div className="px-4 sm:px-5 py-4 sm:py-5">
              {/* Duplikat-Hinweis */}
              {dupe && <div role="alert" className="mb-4 rounded-xl border border-yellow-400/30 bg-yellow-500/10 text-yellow-200 px-3 py-2 flex items-center justify-between gap-3">
                  <div className="text-sm">
                    <span className="font-medium">Kunde existiert bereits:</span>{" "}
                    <span>
                      {dupe.firma ? dupe.firma : [dupe.vorname, dupe.nachname].filter(Boolean).join(" ")}
                      {dupe.ort ? `, ${dupe.ort}` : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AppButton type="button" variant="secondary" onClick={() => navigate(`/kunden/${dupe.id}`)} className="px-3 py-1.5 rounded-lg text-xs bg-yellow-400/20 border border-yellow-300/30 hover:bg-yellow-400/25 transition">
                      Öffnen
                    </AppButton>
                    <AppButton type="button" variant="ghost" onClick={() => setDupe(null)} className={"px-2 py-1.5 rounded-lg text-xs bg-white/10 border border-white/15 hover:bg-white/15 transition"}>
                      Schliessen
                    </AppButton>
                  </div>
                </div>}

              <div className="flex items-center justify-between mb-4">
                <h1 className={`text-xl font-semibold ${"text-blue-100/90"}`}>
                  ➕ Neuer Kunde
                </h1>
                {isDirty && !saving && <span className="text-xs px-2 py-1 rounded bg-yellow-500/10 text-yellow-300 border border-yellow-300/20" aria-live="polite">
                    Eingaben nicht gespeichert
                  </span>}
              </div>

              <KundenFormular formData={form} onChange={handleChange} onSubmit={handleSubmit} onCancel={handleCancel} disabled={saving} aria-busy={saving ? "true" : "false"} />
            </div>
          </div>

          <div className="h-4 md:h-6" />
        </div>
      </div>
    </div>;
}